/**
 * useRestaurantScope
 * ------------------
 * Centralised, single-source helper for injecting `restaurant_id` into
 * every API call that hits a restaurant-scoped backend endpoint.
 *
 * WHY THIS EXISTS
 * ---------------
 * `backend/server.py :: restaurant_scope()` requires an explicit
 * `restaurant_id` for every JWT role that is NOT literally `"restaurant"`
 * (currently: the `corporate` customer role, plus platform-side `admin`,
 * `super_admin`, `sales`). Historically the frontend duplicated the
 * pattern
 *
 *     const params = user.role === "corporate"
 *         ? { restaurant_id: restaurantId } : {};
 *
 * in ~50 call sites. Any forgotten site (e.g. Recipes › save) surfaced
 * as a 400 "restaurant_id is required for corporate users" for a real
 * paying customer. This hook removes the pattern from every call site
 * and guards against the two subtle failure modes that also produced
 * the same 400:
 *
 *   1) `restaurantId` still `null` because AppContext is loading.
 *   2) `restaurantId` was momentarily `null` and a scoped call fires.
 *
 * USAGE
 * -----
 *   const { needsExplicit, restaurantId, scopedParams,
 *           withRestaurant, requireSelection } = useRestaurantScope();
 *
 *   // GET / DELETE — spread into the axios `params` object:
 *   api.get("/inventory-counts", { params: { ...scopedParams } });
 *
 *   // POST / PATCH — wrap the body:
 *   api.post("/recipes", withRestaurant({ name, category }));
 *
 *   // Guard before firing any scoped call. Shows a toast and returns
 *   // false when the current user needs a restaurant but has none
 *   // selected. Callers can early-return on false.
 *   if (!requireSelection()) return;
 *
 * TENANT ISOLATION
 * ----------------
 * The backend `restaurant_scope()` still validates that the passed
 * restaurant_id belongs to the caller's company. This hook ONLY sends
 * the currently-selected restaurant from AppContext — never a value
 * from user input, URL, or another tenant.
 */
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

export function useRestaurantScope() {
    const { user, restaurantId } = useApp();

    // JWT role. `restaurant_admin` / `head_chef` / `kitchen_staff` /
    // `read_only` are all stored as JWT role `"restaurant"` (see
    // `server.py::_validate_customer_user_payload`) so they DO NOT need
    // an explicit restaurant_id — the backend resolves it from the JWT.
    const needsExplicit = !!user && user.role !== "restaurant";

    const scopedParams = useMemo(() => (
        needsExplicit && restaurantId ? { restaurant_id: restaurantId } : {}
    ), [needsExplicit, restaurantId]);

    const withRestaurant = useCallback((body = {}) => {
        if (!needsExplicit) return body;
        if (!restaurantId) return body;               // let the guard handle it
        // Never overwrite an explicit value passed by the caller.
        if (body.restaurant_id) return body;
        return { ...body, restaurant_id: restaurantId };
    }, [needsExplicit, restaurantId]);

    const requireSelection = useCallback(() => {
        if (!needsExplicit) return true;
        if (restaurantId) return true;
        toast.error("Select a restaurant first",
            { description: "Pick one in the top-bar switcher before this action." });
        return false;
    }, [needsExplicit, restaurantId]);

    return {
        needsExplicit,
        restaurantId,
        scopedParams,
        withRestaurant,
        requireSelection,
    };
}
